// @ts-nocheck
import {
  Button,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  // Imperative provider APIs (require the matching provider mounted at app root)
  toast,
  alert,
  banner,
  modal,
  notify,
} from '@/components/ui';

export default function ProvidersPage() {
  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Providers &amp; Actions (toast / alert / banner / modal)</Title>
        <Text c="dimmed" size="sm">
          App-level context providers and their imperative helper APIs. These helpers
          (<Code>toast</Code>, <Code>alert</Code>, <Code>banner</Code>, <Code>modal</Code>,{' '}
          <Code>notify</Code>) are callable from anywhere once the matching provider is mounted at
          the app root. The buttons below fire the live helpers with inline mock content.
        </Text>
      </Stack>

      {/* Toast (AlertProvider) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>toast (AlertProvider)</Title>
            <Text size="sm" c="dimmed">
              Lightweight, auto-dismissing notifications stacked in a corner. Provided by{' '}
              <Code>AlertProvider</Code>. Signature: <Code>toast.success(title, message?, options?)</Code>.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Examples:</Text>
            <Group>
              <Button
                variant="light"
                color="green"
                onClick={() => toast.success('Saved', 'Your changes have been saved.')}
              >
                Success toast
              </Button>
              <Button
                variant="light"
                color="red"
                onClick={() => toast.error('Upload failed', 'The file exceeded the size limit.')}
              >
                Error toast
              </Button>
              <Button
                variant="light"
                color="orange"
                onClick={() => toast.warning('Heads up', 'This action cannot be undone.')}
              >
                Warning toast
              </Button>
              <Button
                variant="light"
                color="blue"
                onClick={() =>
                  toast.info('New version available', 'Refresh to get the latest build.', {
                    action: { label: 'Refresh', onClick: () => {} },
                  })
                }
              >
                Info toast (with action)
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { toast } from '@/components/ui';

toast.success('Saved', 'Your changes have been saved.');
toast.error('Upload failed', 'The file exceeded the size limit.');
toast.warning('Heads up', 'This action cannot be undone.');
toast.info('New version available', 'Refresh to get the latest build.', {
  action: { label: 'Refresh', onClick: () => reloadApp() },
});`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Methods:</Text>
            <Text size="xs" c="dimmed" component="div">
              • success / error / warning / info - (title, message?, options?)
              <br />
              • custom - (options) for a fully custom toast
              <br />
              • promise - (promise, &#123; loading, success, error &#125;) tracks an async task
              <br />
              • dismiss(id) / dismissAll()
              <br />
              • options - position, duration, gradient, gradientVariant, icon, action, onClose
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Alert (AlertProvider — modal dialogs) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>alert (AlertProvider)</Title>
            <Text size="sm" c="dimmed">
              Promise-based modal dialogs for confirmations and prompts. Provided by{' '}
              <Code>AlertProvider</Code>. <Code>alert.confirm</Code> resolves to a boolean;{' '}
              <Code>alert.prompt</Code> resolves to the entered string (or null).
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Examples:</Text>
            <Group>
              <Button
                variant="light"
                color="green"
                onClick={() => alert.success('All set', 'Your profile is up to date.')}
              >
                Success alert
              </Button>
              <Button
                variant="light"
                color="red"
                onClick={() => alert.error('Something went wrong', 'We could not reach the server.')}
              >
                Error alert
              </Button>
              <Button
                variant="light"
                onClick={() =>
                  alert.confirm('Delete project?', 'This will permanently remove "Project Alpha".')
                }
              >
                Confirm dialog
              </Button>
              <Button
                variant="light"
                onClick={() =>
                  alert.prompt('Rename item', 'Enter a new name for this document.', {
                    input: { placeholder: 'New name', defaultValue: 'Untitled' },
                  })
                }
              >
                Prompt dialog
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { alert } from '@/components/ui';

await alert.success('All set', 'Your profile is up to date.');

const ok = await alert.confirm(
  'Delete project?',
  'This will permanently remove "Project Alpha".'
);
if (ok) deleteProject();

const name = await alert.prompt('Rename item', 'Enter a new name.', {
  input: { placeholder: 'New name', defaultValue: 'Untitled' },
});`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Methods:</Text>
            <Text size="xs" c="dimmed" component="div">
              • success / error / warning / info - (title, message, options?) → Promise&lt;void&gt;
              <br />
              • confirm - (title, message, options?) → Promise&lt;boolean&gt;
              <br />
              • prompt - (title, message, options?) → Promise&lt;string | null&gt;
              <br />
              • custom - (options) → Promise&lt;any&gt;
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Banner (AlertProvider) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>banner (AlertProvider)</Title>
            <Text size="sm" c="dimmed">
              Full-width banners pinned to the top or bottom of the viewport, ideal for system-wide
              notices. Provided by <Code>AlertProvider</Code>. Signature:{' '}
              <Code>banner.info(message, options?)</Code>.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Examples:</Text>
            <Group>
              <Button
                variant="light"
                color="blue"
                onClick={() =>
                  banner.info('Scheduled maintenance on Sunday at 02:00 UTC.', {
                    dismissible: true,
                  })
                }
              >
                Info banner
              </Button>
              <Button
                variant="light"
                color="green"
                onClick={() => banner.success('Your subscription was renewed successfully.')}
              >
                Success banner
              </Button>
              <Button
                variant="light"
                color="orange"
                onClick={() =>
                  banner.warning('Your trial ends in 3 days.', {
                    action: { label: 'Upgrade', onClick: () => {} },
                  })
                }
              >
                Warning banner (with action)
              </Button>
              <Button
                variant="light"
                color="red"
                onClick={() =>
                  banner.error('We are experiencing connectivity issues.', { position: 'bottom' })
                }
              >
                Error banner (bottom)
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { banner } from '@/components/ui';

banner.info('Scheduled maintenance on Sunday at 02:00 UTC.', {
  dismissible: true,
});

banner.warning('Your trial ends in 3 days.', {
  action: { label: 'Upgrade', onClick: () => goToBilling() },
});

banner.error('We are experiencing connectivity issues.', {
  position: 'bottom',
});`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Methods:</Text>
            <Text size="xs" c="dimmed" component="div">
              • success / error / warning / info - (message, options?) → id
              <br />
              • dismiss(id?)
              <br />
              • options - title, position ('top' | 'bottom'), dismissible, persistent, duration,
              gradient, action, withBackdrop, withBlur
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Modal (ModalsProvider) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>modal (ModalsProvider)</Title>
            <Text size="sm" c="dimmed">
              Pre-built confirm and delete modals plus passthrough access to Mantine&apos;s modal
              manager. Provided by <Code>ModalsProvider</Code>. Uses callback handlers rather than
              promises.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Examples:</Text>
            <Group>
              <Button
                variant="light"
                onClick={() =>
                  modal.confirm({
                    title: 'Confirm action',
                    body: 'Are you sure you want to publish this article?',
                    onConfirm: () => {},
                    confirmLabel: 'Yes, publish',
                    cancelLabel: 'Cancel',
                  })
                }
              >
                Confirm modal
              </Button>
              <Button
                variant="light"
                color="red"
                onClick={() =>
                  modal.delete({
                    itemName: 'Project Alpha',
                    onDelete: () => {},
                  })
                }
              >
                Delete modal
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { modal } from '@/components/ui';

modal.confirm({
  title: 'Confirm action',
  body: 'Are you sure you want to publish this article?',
  onConfirm: () => publishArticle(),
  confirmLabel: 'Yes, publish',
  cancelLabel: 'Cancel',
});

modal.delete({
  itemName: 'Project Alpha',
  onDelete: () => deleteProject(),
});`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Methods:</Text>
            <Text size="xs" c="dimmed" component="div">
              • confirm - (&#123; title, body, onConfirm, confirmLabel?, cancelLabel?, confirmColor? &#125;)
              <br />
              • delete - (&#123; itemName, onDelete &#125;)
              <br />
              • open / close(id) / closeAll - Mantine modal manager passthrough
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* notify (NotificationProvider) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>notify (NotificationProvider)</Title>
            <Text size="sm" c="dimmed">
              Mantine-backed notification system with built-in loading/update lifecycle. Provided by{' '}
              <Code>NotificationProvider</Code>. Signature:{' '}
              <Code>notify.success(title, message?)</Code>.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Examples:</Text>
            <Group>
              <Button
                variant="light"
                color="green"
                onClick={() => notify.success('Done', 'Record created successfully.')}
              >
                Success notification
              </Button>
              <Button
                variant="light"
                color="red"
                onClick={() => notify.error('Failed', 'Could not delete the record.')}
              >
                Error notification
              </Button>
              <Button
                variant="light"
                color="blue"
                onClick={() => notify.info('FYI', 'Your export is ready to download.')}
              >
                Info notification
              </Button>
              <Button
                variant="light"
                color="yellow"
                onClick={() => notify.warning('Careful', 'You have unsaved changes.')}
              >
                Warning notification
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { notify } from '@/components/ui';

notify.success('Done', 'Record created successfully.');
notify.error('Failed', 'Could not delete the record.');

// Loading lifecycle (uses a shared id)
notify.loading('upload', 'Uploading...', 'Please wait');
notify.update('upload', 'Done!', 'File uploaded', true);`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Methods:</Text>
            <Text size="xs" c="dimmed" component="div">
              • success / error / info / warning - (title, message?)
              <br />
              • loading(id, title, message?) / update(id, title, message?, success?)
              <br />
              • hide(id) / clean()
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Providers reference (wiring) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Provider Wiring</Title>
            <Text size="sm" c="dimmed">
              The imperative helpers above only work because their providers wrap the app. These are
              normally mounted once at the root and are already active on this Demo page.
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Card withBorder p="md" radius="md">
              <Stack gap={4}>
                <Text size="sm" fw={600}>ThemeProvider</Text>
                <Text size="xs" c="dimmed">
                  Mantine theme + color scheme, driven by the preferences store. Injects gradient
                  and semantic CSS variables.
                </Text>
              </Stack>
            </Card>
            <Card withBorder p="md" radius="md">
              <Stack gap={4}>
                <Text size="sm" fw={600}>AlertProvider</Text>
                <Text size="xs" c="dimmed">
                  Powers <Code>toast</Code>, <Code>alert</Code>, and <Code>banner</Code> via a single
                  imperative API.
                </Text>
              </Stack>
            </Card>
            <Card withBorder p="md" radius="md">
              <Stack gap={4}>
                <Text size="sm" fw={600}>NotificationProvider</Text>
                <Text size="xs" c="dimmed">
                  Powers <Code>notify</Code> on top of Mantine notifications, including the loading
                  lifecycle.
                </Text>
              </Stack>
            </Card>
            <Card withBorder p="md" radius="md">
              <Stack gap={4}>
                <Text size="sm" fw={600}>ModalsProvider</Text>
                <Text size="xs" c="dimmed">
                  Powers <Code>modal</Code> with pre-built confirm/delete modals and the Mantine
                  modal manager.
                </Text>
              </Stack>
            </Card>
          </SimpleGrid>

          <Divider />

          <Stack gap="xs">
            <Text size="sm" fw={500}>Setup:</Text>
            <Code block language="tsx">
{`import {
  ThemeProvider,
  NotificationProvider,
  ModalsProvider,
  AlertProvider,
} from '@/components/ui';

function App() {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <ModalsProvider>
          <AlertProvider>
            <YourApp />
          </AlertProvider>
        </ModalsProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
